/**
 * Module-entitlement gate — the single, fail-closed decision function every
 * shell surface (sidebar, browse catalogue, search, command palette, recents,
 * widgets, deep-link routing) consumes before it renders, focuses, or resolves
 * a module.
 *
 * WHY THIS EXISTS
 * ---------------
 * The previous behaviour was default-ALLOW: while the entitlement evaluation
 * was in flight the shell showed the entire catalogue, and any evaluator error
 * (network failure, 5xx, malformed body) was silently converted into "allowed".
 * That is backwards — an unresolved or failed authorization answer must never
 * be rendered as permission. This module inverts it:
 *
 *   • loading      → nothing is permitted; callers render an explicit
 *                    loading state (not the catalogue).
 *   • unavailable  → nothing is permitted; callers render an explicit
 *                    "entitlements could not be verified" state. A failure of
 *                    the entitlement service NEVER reveals the catalogue, in
 *                    any build mode.
 *   • ready        → exactly the server-allowed set, narrowed further by the
 *                    active nav mode (persona / industry workspace).
 *
 * DEVELOPMENT ALLOWANCE
 * ---------------------
 * The codebase already carries one deliberate development convention: a
 * `dev-token-*` bearer (the gateway's local bypass, used by the Playwright auth
 * setup) short-circuits to wildcard permissions — see
 * `packages/hooks/src/usePermissionContext.tsx`. `resolveModuleAccess` honours
 * exactly that convention and nothing wider: a wildcard session skips
 * entitlement evaluation entirely rather than being handed an "allow" on
 * failure. Crucially the wildcard branch is evaluated AFTER the failure branch,
 * so if an evaluation did run and failed, even a development session fails
 * closed.
 *
 * Everything here is pure so it can be unit-tested without a DOM.
 */

/** Lifecycle of the batch entitlement evaluation owned by NavModeContext. */
export type EntitlementPhase =
  /** No evaluation has been started (signed out, or a wildcard dev session). */
  | 'idle'
  /** A batch is in flight; no authoritative answer exists yet. */
  | 'loading'
  /** Every target resolved to an authoritative allow/deny. */
  | 'ready'
  /** At least one target could not be resolved — treat as no answer at all. */
  | 'unavailable';

/** Per-target outcome of one entitlement evaluation. */
export type EntitlementDecision =
  | 'allow'
  | 'deny'
  /** Transport failure, non-OK status, or a body we cannot trust. */
  | 'indeterminate';

/** The minimal shape of an evaluator response `resolveEvaluateDecision` reads. */
export interface EvaluateOutcome {
  /** True when a response was received AND its status was 2xx. */
  ok: boolean;
  /** HTTP status; 0 when the request never completed. */
  status: number;
  /** Parsed body, if the response was JSON-decodable. */
  body?: unknown;
}

/**
 * Classify one evaluator response.
 *
 * The ONLY outcome that grants access is a 2xx whose body carries a literal
 * `allowed: true`. A non-OK status — including 401/403, which mean "this
 * principal may not query the evaluator" — is indeterminate, never an allow. A
 * 2xx whose `allowed` field is missing or not a boolean is also indeterminate:
 * a shape we cannot read is not a permission grant.
 */
export function resolveEvaluateDecision(outcome: EvaluateOutcome): EntitlementDecision {
  if (!outcome.ok) return 'indeterminate';
  const body = outcome.body;
  if (typeof body !== 'object' || body === null) return 'indeterminate';
  const allowed = (body as { allowed?: unknown }).allowed;
  if (typeof allowed !== 'boolean') return 'indeterminate';
  return allowed ? 'allow' : 'deny';
}

/**
 * Fold a batch of per-target decisions into a phase + the allowed id set.
 *
 * A single indeterminate decision poisons the whole batch: we cannot render a
 * partially-verified catalogue and call it authorized, because the operator
 * cannot tell which half was checked. An empty batch is `ready` with an empty
 * set — there was nothing to authorize, so nothing is permitted.
 */
export function reduceEntitlementBatch(
  decisions: ReadonlyArray<readonly [string, EntitlementDecision]>,
): { phase: 'ready' | 'unavailable'; allowed: Set<string> } {
  const allowed = new Set<string>();
  for (const [id, decision] of decisions) {
    if (decision === 'indeterminate') {
      return { phase: 'unavailable', allowed: new Set<string>() };
    }
    if (decision === 'allow') allowed.add(id);
  }
  return { phase: 'ready', allowed };
}

/* ------------------------------------------------------------------------- *
 * The self-scoped entitlement contract
 * ------------------------------------------------------------------------- */

/**
 * Relative path of the caller-scoped batch entitlement read. The shared API
 * client prepends `/api/v1`, so the literal must NOT repeat it (the
 * release-blocker audit forbids the redundant prefix).
 *
 * Backed by `POST /api/v1/auth/entitlements/evaluate`, which answers for the
 * CALLING principal: roles, edition, and feature flags are derived server-side
 * from the authenticated principal and tenant, and the request carries no
 * principal at all. The shell previously read the ADMIN rules-preview route
 * (`/admin/entitlement-rules/evaluate`), which takes a synthetic principal and
 * is gated by `Admin.Full` — so a genuine non-admin session got 403, which the
 * gate correctly (and uselessly) treats as indeterminate.
 */
export const SELF_ENTITLEMENT_PATH = '/auth/entitlements/evaluate';

/** Catalogue scope a module id is evaluated under, server-side. */
export const MODULE_SCOPE = 'Module';
/** Catalogue scope a widget id is evaluated under, server-side. */
export const WIDGET_SCOPE = 'Widget';

/** The targets one batch asks about. */
export interface SelfEntitlementRequest {
  moduleIds: readonly string[];
  widgetIds: readonly string[];
}

/** Folded result of one batch: a phase plus the per-scope allowed sets. */
export interface SelfEntitlementResult {
  phase: 'ready' | 'unavailable';
  modules: ReadonlySet<string>;
  widgets: ReadonlySet<string>;
}

/**
 * Minimal transport the batch needs: POST the request body to
 * {@link SELF_ENTITLEMENT_PATH} and resolve the decoded response. The caller
 * owns the path so the API-client call site carries the route literal (which is
 * what proves the endpoint has a real operator caller). The shared client throws
 * on 401/403/4xx/5xx and on a body it cannot decode, so every failure surfaces
 * here as a rejection — which `fetchSelfEntitlements` turns into `unavailable`,
 * never an allow.
 */
export type SelfEntitlementPost = (body: unknown) => Promise<unknown>;

const EMPTY_ID_SET: ReadonlySet<string> = new Set<string>();

const UNAVAILABLE_RESULT: SelfEntitlementResult = {
  phase: 'unavailable',
  modules: EMPTY_ID_SET,
  widgets: EMPTY_ID_SET,
};

/** Index a batch body's `decisions` array by `<scope>::<targetId>`. */
function indexDecisions(body: unknown): Map<string, unknown> | null {
  if (typeof body !== 'object' || body === null) return null;
  const decisions = (body as { decisions?: unknown }).decisions;
  if (!Array.isArray(decisions)) return null;
  const index = new Map<string, unknown>();
  for (const row of decisions) {
    if (typeof row !== 'object' || row === null) continue;
    const { scope, targetId } = row as { scope?: unknown; targetId?: unknown };
    if (typeof scope !== 'string' || typeof targetId !== 'string') continue;
    index.set(`${scope}::${targetId}`, row);
  }
  return index;
}

/**
 * Fold a batch RESPONSE BODY into the gate's inputs.
 *
 * Fails closed on every shape we cannot fully trust:
 *   • a body that is not an object, or carries no `decisions` array;
 *   • a requested target with no row in the answer — an unanswered question is
 *     not permission, and a partially-answered catalogue cannot be rendered as
 *     authorized (the operator could not tell which half was checked);
 *   • a row whose `allowed` is missing or not a boolean.
 *
 * Each row is classified by the same `resolveEvaluateDecision` the single-target
 * path uses, and folded by the same `reduceEntitlementBatch`, so the batch
 * contract cannot drift from the per-target one.
 */
export function resolveSelfEntitlementBatch(
  body: unknown,
  request: SelfEntitlementRequest,
): SelfEntitlementResult {
  const index = indexDecisions(body);
  if (!index) return UNAVAILABLE_RESULT;

  const classify = (
    scope: string,
    ids: readonly string[],
  ): Array<readonly [string, EntitlementDecision]> =>
    ids.map((id) => {
      const row = index.get(`${scope}::${id}`);
      // A target we asked about and did not get back is indeterminate.
      if (row === undefined) return [id, 'indeterminate'] as const;
      return [id, resolveEvaluateDecision({ ok: true, status: 200, body: row })] as const;
    });

  const modules = reduceEntitlementBatch(classify(MODULE_SCOPE, request.moduleIds));
  const widgets = reduceEntitlementBatch(classify(WIDGET_SCOPE, request.widgetIds));

  if (modules.phase === 'unavailable' || widgets.phase === 'unavailable') {
    return UNAVAILABLE_RESULT;
  }
  return { phase: 'ready', modules: modules.allowed, widgets: widgets.allowed };
}

/**
 * Run one self-scoped batch through `post` and fold the answer.
 *
 * An empty catalogue short-circuits without a request: there is nothing to
 * authorize, so the phase is `ready` with nothing permitted (never a blanket
 * allow). Any rejection — transport failure, 401, 403, 5xx, undecodable body —
 * resolves to `unavailable`. There is deliberately no branch that can turn a
 * failure into an allow.
 */
export async function fetchSelfEntitlements(
  post: SelfEntitlementPost,
  request: SelfEntitlementRequest,
): Promise<SelfEntitlementResult> {
  if (request.moduleIds.length === 0 && request.widgetIds.length === 0) {
    return { phase: 'ready', modules: EMPTY_ID_SET, widgets: EMPTY_ID_SET };
  }
  let body: unknown;
  try {
    body = await post({
      moduleIds: [...request.moduleIds],
      widgetIds: [...request.widgetIds],
    });
  } catch {
    return UNAVAILABLE_RESULT;
  }
  return resolveSelfEntitlementBatch(body, request);
}

/** What a surface should render for the current gate. */
export type ModuleAccessState = 'loading' | 'unavailable' | 'ready';

export interface ModuleAccessInput {
  phase: EntitlementPhase;
  /** Ids the evaluator authoritatively allowed; null unless `phase === 'ready'`. */
  entitledModuleIds: ReadonlySet<string> | null;
  /**
   * Nav-mode (persona / industry workspace) narrowing, or null for "no mode
   * filter". This never widens entitlement — it only narrows further.
   */
  modeVisibleModuleIds?: ReadonlySet<string> | null;
  /** Every module the manifest registered — the widest possible catalogue. */
  catalogueModuleIds: readonly string[];
  /** True for the `dev-token-*` wildcard development session. */
  wildcardSession?: boolean;
}

export interface ModuleAccess {
  state: ModuleAccessState;
  /** Empty unless `state === 'ready'`. */
  permittedModuleIds: ReadonlySet<string>;
}

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/**
 * The gate. Order of the branches is the security contract:
 *
 *   1. an entitlement-service failure denies everything — including for a
 *      development session, so a failure can never reveal the catalogue;
 *   2. only then does the wildcard development allowance apply;
 *   3. anything not yet resolved is a loading state, not an allow;
 *   4. a resolved batch permits exactly the allowed ∩ catalogue ∩ mode set.
 */
export function resolveModuleAccess(input: ModuleAccessInput): ModuleAccess {
  if (input.phase === 'unavailable') {
    return { state: 'unavailable', permittedModuleIds: EMPTY_SET };
  }

  const narrow = (ids: readonly string[]): Set<string> => {
    const mode = input.modeVisibleModuleIds;
    return new Set(ids.filter((id) => !mode || mode.has(id)));
  };

  if (input.wildcardSession) {
    return { state: 'ready', permittedModuleIds: narrow(input.catalogueModuleIds) };
  }

  if (input.phase === 'idle' || input.phase === 'loading') {
    return { state: 'loading', permittedModuleIds: EMPTY_SET };
  }

  // phase === 'ready'. A ready phase with no set is a contradiction; treat it
  // as unverified rather than assuming either direction.
  const entitled = input.entitledModuleIds;
  if (!entitled) {
    return { state: 'unavailable', permittedModuleIds: EMPTY_SET };
  }

  return {
    state: 'ready',
    permittedModuleIds: narrow(input.catalogueModuleIds.filter((id) => entitled.has(id))),
  };
}

/**
 * The one predicate every surface calls. Deliberately total: an unknown /
 * undefined moduleId is not permitted, and nothing is permitted unless the
 * gate is `ready`.
 */
export function isModulePermitted(
  access: ModuleAccess,
  moduleId: string | null | undefined,
): boolean {
  if (access.state !== 'ready') return false;
  if (!moduleId) return false;
  return access.permittedModuleIds.has(moduleId);
}

/**
 * Filter any module-tagged records (views, actions, widget definitions, recent
 * entries, help topics) down to the permitted set. Records with no moduleId are
 * dropped when `keepUntagged` is false — the safe default for anything that
 * navigates.
 */
export function filterByModuleAccess<T>(
  access: ModuleAccess,
  records: readonly T[],
  moduleIdOf: (record: T) => string | null | undefined,
  keepUntagged = false,
): T[] {
  if (access.state !== 'ready') return [];
  return records.filter((record) => {
    const moduleId = moduleIdOf(record);
    if (!moduleId) return keepUntagged;
    return access.permittedModuleIds.has(moduleId);
  });
}

/**
 * The development allowance predicate, mirroring
 * `PermissionsProvider`'s dev-token short-circuit. Kept here (rather than
 * inlined at the call site) so there is exactly one definition of "development
 * session" in the shell's authorization path.
 */
export function isWildcardDevSession(token: string | null | undefined): boolean {
  return typeof token === 'string' && token.startsWith('dev-token-');
}
